import React, { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { FlexibleModal } from '../../../modal/flexible-modal';
import { Toaster } from '../../../toaster';
import { BlockerFunction, useBlocker } from 'react-router';

export type FormConsistencyProps = {
  setIsFormModified?: Dispatch<SetStateAction<boolean>>;
  isFormModified?: boolean;
  setIsFormToBeSaved?: Dispatch<SetStateAction<boolean>>;
  isFormToBeSaved?: boolean;
  nextSaveActionName?: string;
  setNextSaveActionName?: Dispatch<SetStateAction<string>>;
  nextSaveActionBody?: ReactElement;
  setNextSaveActionBody?: Dispatch<SetStateAction<ReactElement>>;
  nextSaveAction?: any;
  setNextSaveAction?: (action: any) => void;
};

export const FormPrompt: React.FC<React.PropsWithChildren<FormConsistencyProps>> = ({
  setIsFormModified = () => {},
  isFormToBeSaved = false,
  setIsFormToBeSaved = () => {},
  nextSaveActionName = '',
  nextSaveActionBody,
  nextSaveAction = () => {},
}) => {
  const { submitForm, dirty, isSubmitting, isValid } = useFormikContext<any>();
  const { t } = useTranslation(['translation']);

  useEffect(() => {
    setIsFormModified(dirty);
  }, [dirty, setIsFormModified]);

  const showModal = isFormToBeSaved && dirty;
  const [sModal, setSModal] = useState(false);

  const handleFormConfirm = useCallback(
    (callbackConfirm: any) => {
      setIsFormToBeSaved(false);
      if (!isSubmitting && dirty) {
        setSModal(false);
        submitForm()
          .then(() => {
            if (isValid) {
              callbackConfirm();
            } else {
              Toaster.error(t('notifications.errors.invalidForm'));
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
    [t, dirty, isValid, isSubmitting, submitForm, setIsFormToBeSaved]
  );

  const handleFormCancel = useCallback(() => {
    setIsFormToBeSaved(false);
  }, [setIsFormToBeSaved]);

  const Modal: React.FC<
    React.PropsWithChildren<{
      show: boolean;
      onConfirm: any;
      onSecondaryConfirm?: any;
      onCancel: any;
      title: string;
      confirmAction: string;
      secondConfirmAction?: string;
      cancelAction: string;
    }>
  > = ({
    show,
    onConfirm,
    onSecondaryConfirm,
    onCancel,
    title = t('modal.modified.title'),
    confirmAction,
    secondConfirmAction,
    cancelAction,
  }) => {
    return (
      <FlexibleModal
        show={show}
        title={title}
        confirmButtonMessage={confirmAction}
        secondaryConfirmButtonMessage={secondConfirmAction}
        cancelButtonMessage={cancelAction}
        onConfirm={onConfirm}
        onSecondaryConfirm={onSecondaryConfirm}
        onCancel={onCancel}
      >
        <>
          {isFormToBeSaved && nextSaveActionBody && (
            <>
              <p style={{ fontWeight: 'bold', color: 'red' }}>{t('modal.modified.body.action')}</p>
              <p>{nextSaveActionBody}</p>
            </>
          )}

          {(!isFormToBeSaved || !nextSaveActionBody) && <p>{t('modal.modified.body.leave')}</p>}
        </>
      </FlexibleModal>
    );
  };

  const shouldBlock = useCallback<BlockerFunction>(() => {
    const block = !isSubmitting && dirty;
    setSModal(block);
    return block;
  }, [dirty, isSubmitting]);

  const blocker = useBlocker(shouldBlock);

  return (
    <>
      {!isSubmitting && (
        <>
          <Modal
            show={blocker.state === 'blocked' && sModal}
            onConfirm={() => handleFormConfirm(blocker.proceed)}
            onSecondaryConfirm={blocker.proceed}
            onCancel={blocker.reset}
            confirmAction={t('modal.modified.confirmButton.leave')}
            secondConfirmAction={t('modal.modified.cancelButton.leave')}
            cancelAction={t('modal.modified.cancelButton.close')}
            title={t('modal.modified.title')}
          />

          <Modal
            show={showModal}
            title={nextSaveActionName}
            confirmAction={t('modal.modified.confirmButton.action') + nextSaveActionName}
            cancelAction={t('modal.modified.cancelButton.action')}
            onConfirm={() => handleFormConfirm(nextSaveAction)}
            onCancel={() => handleFormCancel()}
          />
        </>
      )}
    </>
  );
};
